import { Component, Input } from '@angular/core';
import { InputType } from 'enums/InputType';
import { OrderLineModel } from 'models/generated/Entities.GormCrm.OrderLine';
import { ProductModel } from 'models/generated/Entities.GormCrm.Product';
import { ProductVariantsOptionModel } from 'models/generated/Entities.GormCrm.ProductVariantsOption';
import { VariantListModel } from 'models/generated/Entities.GormCrm.VariantList';
import { VariantListOptionModel } from 'models/generated/Entities.GormCrm.VariantListOption';
import { SelectListItemModel } from 'models/SelectList';
import { Inputs } from 'modules/inputs';
import { Layout } from 'modules/layout';
import { Pipes } from 'modules/pipes';
import { PaymentIntervalPipe } from 'pipes/payment-interval.pipe';
import { ProductService } from 'services/product.service';

interface VariantListInfo {
	list: VariantListModel;
	listOptions: SelectListItemModel[];
}

@Component({
	selector: 'edit-order-line',
	templateUrl: 'edit-order-line.component.html',
	imports: [Layout, Inputs, Pipes]
})
export class EditOrderLineComponent {

	@Input('orderLine') public orderLine!: OrderLineModel;

	public availableIntervals: SelectListItemModel[];
	public inputType = InputType;
	public productList?: SelectListItemModel[];
	public variantLists?: VariantListInfo[];
	public totalPriceHasBeenEdited = false;

	// Selected product and variantlist options, held in memory to properly handle switching between products without always having a matching productVariant
	public selectedProduct?: ProductModel;
	private selectedOptions: VariantListOptionModel[] = [];

	private _defaultNumberOfInstallments = 1;
	@Input('defaultNumberOfInstallments') public set defaultNumberOfInstallments(value: number) {
		this._defaultNumberOfInstallments = value;
		this.availableIntervals = this.getIntervalOptions();
	}

	constructor(public readonly productService: ProductService, private readonly paymentInterval: PaymentIntervalPipe) {

		this.availableIntervals = this.getIntervalOptions();

		this.productService.getAllActiveProducts(false).then(availableProducts => {
			
			this.productList = availableProducts.map(product => new SelectListItemModel(product, product.name));

			this.selectedProduct = availableProducts.find(p => p.id === this.orderLine.productVariant?.productId);
			if (!this.selectedProduct) return; // New order line, no product selected yet

			if (this.orderLine.totalPrice != undefined) this.totalPriceHasBeenEdited = true;

			// Update edited model with object this component's object references
			this.orderLine.productVariant = this.selectedProduct.productVariants.find(v => v.id === this.orderLine.productVariant?.id);
			// Determine initially selected options
			if (this.orderLine.productVariant) this.selectedOptions = this.orderLine.productVariant.productVariantsOptions.map(pvo => pvo.variantOption!);
			// Update available variant lists based on selected product
			this.updateVariantLists();
		});
	}

	public selectProduct(product: ProductModel) {
		this.selectedProduct = product;				// Update selected product, regardless of whether a variant gets selected
		this.updateVariantLists();					// Update available variant lists based on selected product
		this.selectVariantBasedOnSelectedOptions(); // Update productVariant reference based on new product selection
		if (!product.autoRenew) this.setPaymentInterval(0); // Force "upfront" payment when a product isn't autorenewable
	}

	public getSelectedListOption(variantList: VariantListModel) {
		return this.selectedOptions.find(option => option.variantList == variantList);
	}

	public selectListOption(selectedOption: VariantListOptionModel) {
		// Selected options is all selected options for other VariantLists, plus the one selected now
		this.selectedOptions = [...this.selectedOptions.filter(option => option.variantList != selectedOption.variantList), selectedOption];
		this.selectVariantBasedOnSelectedOptions();
	}

	private selectVariantBasedOnSelectedOptions() {
		// Find the product variant where every option is currently selected.
		// If all lists doesn't have a matching selected option, no productVariant is selected, and the orderLine can't be saved.
		this.orderLine.productVariant = this.selectedProduct!.productVariants.find(v => v.productVariantsOptions.every(option => this.selectedOptions.indexOf(option.variantOption!) >= 0));
		this.orderLine.productVariantId = this.orderLine.productVariant?.id;

		if (this.totalPriceHasBeenEdited) return; // Don't update total price if it has been edited manually
		this.orderLine.totalPrice = this.orderLine.productVariant?.price ?? 0; // Update total price based on selected product variant
	}

	private updateVariantLists() {
		if (!this.selectedProduct) return;

		// Update available variant lists based on selected product
		const availableProductVariantOptions = this.selectedProduct.productVariants.flatMap(pv => pv.productVariantsOptions); // All options available on the selected product
		const distinctLists = new Set(availableProductVariantOptions.flatMap(pvo => pvo.variantOption!.variantList!)); // Any list containing any of those options

		this.variantLists = Array.from(distinctLists)
			.sort((a, b) => b.type - a.type)
			.map(list => this.getVariantListInfoWithFilteredOptions(list, availableProductVariantOptions));
	}

	private getVariantListInfoWithFilteredOptions(variantList: VariantListModel, availableOptions: ProductVariantsOptionModel[]) {
		return {
			list: variantList,
			listOptions: variantList.variantListOptions
				.filter(vlo => vlo.productVariantsOptions.some(pvo => availableOptions.indexOf(pvo) >= 0))
				.map(option => new SelectListItemModel(option, option.name))
		};
	}

	private getIntervalOptions() {
		return [
			new SelectListItemModel(1, this.paymentInterval.transform(1)),
			new SelectListItemModel(3, this.paymentInterval.transform(3)),
			new SelectListItemModel(12, this.paymentInterval.transform(12)),
			new SelectListItemModel(0, this.paymentInterval.transform(1, { installments: this._defaultNumberOfInstallments })),
		];
	}
	public setPaymentInterval(interval: number) {
		if (interval === 0) {
			this.orderLine.numberOfInstallments = this._defaultNumberOfInstallments;
			this.orderLine.paymentInterval = 1; // Upfront Always monthly payments of upfront
			this.orderLine.startupAmountExclFirstInvoice = undefined; // When paying upfront, having a startup amount doesn't make sense
		}
		else {
			this.orderLine.numberOfInstallments = undefined;
			this.orderLine.paymentInterval = interval;
		}
	}
}
